import { Component } from 'react';
import { onLCP, onFID, onCLS, onTTFB, onFCP, Metric, INPAttribution } from 'web-vitals';
import { onINP } from 'web-vitals/attribution';

interface IProps {
  mtestid?: string;
}

// INPMetricWithAttribution が提供されているが型エラーとなるので独自で型を定義しています
// https://github.com/GoogleChrome/web-vitals/blob/10b584593060ede911f409e01ef7871fe662d226/src/types/inp.ts#L62-L67
interface MetricWithINPAttribution extends Metric {
  attribution?: INPAttribution;
}

export default class YahooAnalytics extends Component<IProps> {
  private ya!: YA;
  /**
   *
   */
  public componentDidMount() {
    // ブラウザ環境でのみ動くためマウントされてから読み込む
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    this.ya = require('@yj-intl/yas-logger-ya');
    // 特に秘密情報ではないのでそのまま設定
    this.ya('init', '52b09101ac194587aaacfdccd791ee37', 'c0442949-c31a-4adb-ac5a-07372f0afd38');
    this.ya('global', { mtestid: this.props.mtestid });

    const hitYA = () => {
      this.ya('hit', 'pageview,webPerformance');
      // 負荷対策のため、0.1%サンプリングする
      if (Math.random() <= 0.001) {
        this.measureWebVitals();
      }
    };

    // BFCache有効時のブラウザバック・フォワード時の表示時にもpageviewログを落とす
    window.addEventListener('pageshow', this.handleBFCache);

    // 一部条件でテストが通らないため、window.performance.getEntriesByNameの定義を確認してから実行する
    const navigationEntries =
      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
      window.performance &&
      // @ts-ignore テスト実行時にgetEntriesByNameが存在しない場合がある
      window.performance.getEntriesByName &&
      window?.performance?.getEntriesByType('navigation');

    if (document.prerendering) {
      this.ya('hit', 'event', {
        category: 'prerender',
        name: 'render',
        isNonInteraction: true
      });
      document.addEventListener(
        'prerenderingchange',
        () => {
          hitYA();
          this.ya('hit', 'event', {
            category: 'prerender',
            name: 'view',
            isNonInteraction: true
          });
        },
        {
          once: true
        }
      );
    } else if (navigationEntries?.[0]?.activationStart && navigationEntries?.[0].activationStart > 0) {
      this.ya('hit', 'event', {
        category: 'prerender',
        name: 'rendered',
        isNonInteraction: true
      });
      hitYA();
    } else {
      hitYA();
    }
  }
  /**
   * アンマウント時は設定を破棄する
   */
  public componentWillUnmount() {
    window.removeEventListener('pageshow', this.handleBFCache);
  }
  /**
   *
   */
  public render() {
    return null;
  }
  /**
   * Web Vitals をYahoo! Analytics ( YA ) で可視化する話
   * https://wiki.workers-hub.com/pages/viewpage.action?pageId=649414828
   *
   * @param metric
   */
  private getValue = (metric: Metric) => {
    if (metric.name === 'CLS') {
      return metric.delta * 1000;
    }
    if (metric.name === 'TTFB') {
      const { requestStart } = metric.entries[0] as PerformanceResourceTiming;
      return metric.delta - requestStart;
    }
    return metric.delta;
  };

  private sendToYA = (metric: Metric) => {
    const value = this.getValue(metric);

    const log = {
      category: 'WebVitals',
      action: metric.name,
      name: metric.id,
      value: Math.round(value),
      isNonInteraction: true
    };

    if (this.ya !== null) {
      this.ya('hit', 'event', log);
      if (metric.name !== 'CLS') {
        this.ya('hit', 'timing', log);
      }
    }
  };

  private sendINPToYA = (metric: MetricWithINPAttribution) => {
    const value = this.getValue(metric);

    const log = {
      category: 'WebVitals',
      action: metric.name,
      name: metric.attribution?.eventTarget ?? '',
      value: Math.round(value),
      isNonInteraction: true
    };

    if (this.ya !== null) {
      this.ya('hit', 'event', log);
      this.ya('hit', 'timing', log);
    }
  };

  private measureWebVitals = () => {
    try {
      onLCP(this.sendToYA);
      onFID(this.sendToYA);
      onCLS(this.sendToYA);
      onTTFB(this.sendToYA);
      onFCP(this.sendToYA);
      onINP(this.sendINPToYA);
    } catch {
      // do nothing
    }
  };

  private handleBFCache = (event: PageTransitionEvent) => {
    if (event.persisted) {
      this.ya('hit', 'pageview');
    }
  };
}
